import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import ProductExperience from '@comp/product/product-experience'
import type { Capability } from '@comp/product/product-capabilities'
import type { Step } from '@comp/product/product-how-it-works'

const protocols = [
  'SAML 2.0',
  'OpenID Connect',
  'OAuth 2.0',
  'SCIM',
  'LDAP',
  'Kerberos',
  'RADIUS',
  'JWT',
]

interface HubContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  capsEyebrow: string
  capsTitle: [string, string]
  capsBody: string
  capabilities: Capability[]
  howEyebrow: string
  howTitle: string
  howBody: string
  steps: Step[]
  intEyebrow: string
  intTitle: [string, string]
  intBody: string
  brochureCopy: string
}

const content: Record<'en' | 'tr', HubContent> = {
  en: {
    seoTitle: 'Monosign: Identity & Access Management | Monofor',
    seoDescription:
      "Monosign is Monofor's IAM platform: SSO, MFA, provisioning, lifecycle automation, ITDR, AI agent governance, and reporting, built as one product.",
    heroEyebrow: 'Identity & Access Management',
    heroTitle: ['One identity.', 'Every application.'],
    heroBody:
      'Give your people a simpler way to work. Bring sign-in, authentication, and the identity lifecycle together in one secure platform.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Download product brief',
    capsEyebrow: 'Capabilities',
    capsTitle: ['Everything you need', 'for modern IAM.'],
    capsBody:
      'Monosign collapses single sign-on, multi-factor authentication, lifecycle, and audit into one product, without the integration tax of stitching point tools.',
    capabilities: [
      {
        title: 'Single Sign-On',
        description:
          'One identity, every app. Reduce login friction without compromising security.',
      },
      {
        title: 'Multi-Factor Authentication',
        description:
          'Adaptive MFA tied to risk signals. Phishing-resistant by default.',
      },
      {
        title: 'User Provisioning',
        description:
          'Joiner / mover / leaver workflows automate access across every system.',
      },
      {
        title: 'Password Management',
        description:
          'Self-service resets, rotation, and policy enforcement at scale.',
      },
      {
        title: 'Lifecycle Workflows',
        description:
          'Approval flows, entitlement reviews, and access requests built into the platform.',
      },
      {
        title: 'Audit & Compliance',
        description:
          'Every authentication event logged, exportable, and tamper-evident.',
      },
      {
        title: 'Identity Threat Detection (ITDR)',
        description:
          'MITRE ATT&CK-mapped detections with automated response (revoke, disable, step-up), plus SSF/CAEP signals.',
      },
      {
        title: 'AI Agent Identity',
        description:
          'Govern AI agents and service accounts as first-class identities: owners, guardrails, trust scores, kill-switch.',
      },
      {
        title: 'Reporting & Timeline',
        description:
          'No-SQL report builder, scheduled delivery with signed webhooks, and a timeline of every access change.',
      },
      {
        title: 'Security Score & ISPM',
        description:
          'Explainable risk scores on every identity and app, driving step-up policy, plus an A-F security grade.',
      },
      {
        title: 'Identity Intelligence (IVIP)',
        description:
          'One graph of who can access what: effective access, blast radius, and findings with one-click remediation.',
      },
      {
        title: 'MCP Gateway',
        description:
          'AI agents reach tools through an identity-aware gateway: allow-lists, approvals, budgets, full audit.',
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From legacy stack to unified identity in minutes.',
    howBody:
      'Setup is measured in minutes, not project plans. Here is the path.',
    steps: [
      {
        title: 'Connect',
        description:
          'Plug Monosign into your existing directories, apps, and identity sources; no migration required.',
      },
      {
        title: 'Configure',
        description:
          'Define access policies, MFA rules, and provisioning workflows from one console.',
      },
      {
        title: 'Authenticate',
        description:
          'Every user, every request, every device: verified in milliseconds, logged forever.',
      },
    ],
    intEyebrow: 'Integrations',
    intTitle: ['Speaks every protocol', 'your apps already use.'],
    intBody:
      'Monosign supports the standards your stack already runs, and provides a clean REST API for everything else.',
    brochureCopy:
      'Read the technical breakdown: architecture, deployment options, and the full integration list.',
  },
  tr: {
    seoTitle: 'Monosign: Kimlik ve Erişim Yönetimi (IAM) | Monofor',
    seoDescription:
      "Monosign, Monofor'un IAM platformudur: SSO, MFA, kullanıcı sağlama, yaşam döngüsü otomasyonu, ITDR, AI ajan yönetişimi ve raporlama, hepsi tek üründe.",
    heroEyebrow: 'Kimlik ve Erişim Yönetimi',
    heroTitle: ['Tek kimlik.', 'Tüm uygulamalar.'],
    heroBody:
      'Ekibinizin işini kolaylaştırın. Giriş, kimlik doğrulama ve kimlik yaşam döngüsünü tek güvenli platformda birleştirin.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Ürün dokümanını indir',
    capsEyebrow: 'Yetenekler',
    capsTitle: ['Modern IAM için', 'ihtiyacınız olan her şey.'],
    capsBody:
      'Monosign; tek oturum açma, çok faktörlü kimlik doğrulama, yaşam döngüsü ve denetimi tek üründe birleştirir; nokta çözümleri birbirine dikmenin entegrasyon maliyeti olmadan.',
    capabilities: [
      {
        title: 'Tek Oturum Açma (SSO)',
        description:
          'Tek kimlik, tüm uygulamalar. Güvenlikten ödün vermeden giriş sürtünmesini azaltın.',
      },
      {
        title: 'Çok Faktörlü Kimlik Doğrulama',
        description:
          'Risk sinyallerine bağlı uyarlanabilir MFA. Varsayılan olarak kimlik avına dirençli.',
      },
      {
        title: 'Kullanıcı Sağlama',
        description:
          'İşe giriş / geçiş / ayrılış akışları erişimi tüm sistemlerde otomatikleştirir.',
      },
      {
        title: 'Parola Yönetimi',
        description:
          'Self servis sıfırlama, rotasyon ve ölçekli politika uygulaması.',
      },
      {
        title: 'Yaşam Döngüsü Akışları',
        description:
          'Onay akışları, yetki gözden geçirmeleri ve erişim talepleri platformun içinde.',
      },
      {
        title: 'Denetim ve Uyum',
        description:
          'Her kimlik doğrulama olayı loglanır, dışa aktarılabilir ve kurcalamaya karşı korumalıdır.',
      },
      {
        title: 'Kimlik Tehdit Tespiti (ITDR)',
        description:
          'MITRE ATT&CK eşlemeli tespitler ve otomatik yanıt (oturum iptali, hesap kapatma, step-up), artı SSF/CAEP sinyalleri.',
      },
      {
        title: 'AI Ajan Kimliği',
        description:
          'AI ajanlarını ve servis hesaplarını birinci sınıf kimlik olarak yönetin: sahip, korkuluklar, güven skoru, kill-switch.',
      },
      {
        title: 'Raporlama ve Zaman Çizelgesi',
        description:
          'SQL gerektirmeyen rapor oluşturucu, imzalı webhook’larla zamanlanmış teslimat ve her erişim değişikliğinin zaman çizelgesi.',
      },
      {
        title: 'Güvenlik Skoru ve ISPM',
        description:
          'Her kimlik ve uygulamada açıklanabilir risk skoru, step-up politikaları, artı A-F güvenlik karnesi.',
      },
      {
        title: 'Kimlik İstihbaratı (IVIP)',
        description:
          'Kimin neye erişebildiğinin tek grafiği: etkin erişim, etki alanı analizi ve tek tıkla düzeltilen bulgular.',
      },
      {
        title: 'MCP Gateway',
        description:
          'AI ajanları araçlara kimlik farkındalıklı bir geçitten ulaşır: izin listeleri, onaylar, bütçeler, tam denetim izi.',
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Eski yapıdan birleşik kimliğe dakikalar içinde.',
    howBody:
      'Kurulum proje planıyla değil, dakikayla ölçülür. İzlenecek yol şu.',
    steps: [
      {
        title: 'Bağlanın',
        description:
          'Monosign’ı mevcut dizinlerinize, uygulamalarınıza ve kimlik kaynaklarınıza bağlayın; taşıma gerekmez.',
      },
      {
        title: 'Yapılandırın',
        description:
          'Erişim politikalarını, MFA kurallarını ve sağlama akışlarını tek konsoldan tanımlayın.',
      },
      {
        title: 'Doğrulayın',
        description:
          'Her kullanıcı, her istek, her cihaz: milisaniyeler içinde doğrulanır, kalıcı olarak loglanır.',
      },
    ],
    intEyebrow: 'Entegrasyonlar',
    intTitle: ['Uygulamalarınızın kullandığı', 'her protokolü konuşur.'],
    intBody:
      'Monosign, yığınınızın zaten çalıştırdığı standartları destekler; geri kalan her şey için temiz bir REST API sunar.',
    brochureCopy:
      'Teknik detayları okuyun: mimari, kurulum seçenekleri ve tam entegrasyon listesi.',
  },
}

type Props = { locale: string }

export default function MonosignPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  const basePath = locale === 'tr' ? '/tr' : ''
  return (
    <Layout>
      <Seo
        title={c.seoTitle}
        description={c.seoDescription}
        path={`${basePath}/sign`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/sign`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/sign`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/sign`} />
      </Head>
      <JsonLd
        id="product"
        data={{
          '@type': 'SoftwareApplication',
          name: 'Monosign',
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Web (cloud or self-hosted)',
          description:
            "Monofor's IAM platform: SSO, MFA, user provisioning, password management, and lifecycle automation, built as one product.",
          url: 'https://monofor.com/sign',
          publisher: { '@type': 'Organization', name: 'Monofor' },
        }}
      />
      <ProductExperience product="sign" lang={locale === 'tr' ? 'tr' : 'en'} content={c} protocols={protocols} />
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
